import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ChangeMachine, CustomerMachineryState, DecommissionPreview, Machine, MachinesQuery } from "./models/models";
import customerMachineryApi from "./api/customerMachineryApi";

export const getMachines = createAsyncThunk(
  "customerMachinery/getMachines",
  async (query: MachinesQuery) => {
    return customerMachineryApi.getMachines(query);
  }
);

export const getMachine = createAsyncThunk(
  "customerMachinery/getMachine",
  async (id: string) => {
    return customerMachineryApi.getMachine(id);
  }
)

export const createMachine = createAsyncThunk(
  "customerMachinery/createMachine",
  async (model: ChangeMachine) => {
    return customerMachineryApi.createMachine(model);
  }
);

export const changeMachine = createAsyncThunk(
  "customerMachinery/changeMachine",
  async ({ id, model }: any) => {
    return customerMachineryApi.changeMachine(id, model);
  }
);

export const deleteMachine = createAsyncThunk(
  "customerMachinery/deleteMachine",
  async (id: string) => {
    return customerMachineryApi.deleteMachine(id);
  }
);

export const getManufacturers = createAsyncThunk(
  "customerMachinery/getManufacturers",
  async (machineTypeId: string) => {
    return customerMachineryApi.getManufacturersById(machineTypeId);
  }
);

export const getOperationTypes = createAsyncThunk(
  "customerMachinery/getOperationTypes",
  async (machineTypeId: string) => {
    return customerMachineryApi.getOperationTypes(machineTypeId);
  }
);

export const getAxisConfigurations = createAsyncThunk(
  "customerMachinery/getAxisConfigurations",
  async (operationTypeId: string) => {
    return customerMachineryApi.getAxisConfigurations(operationTypeId);
  }
);

export const getControlSystemBrands = createAsyncThunk(
  "customerMachinery/getControlSystemBrands",
  async (machineTypeId: string) => {
    return customerMachineryApi.getControlSystemBrands(machineTypeId);
  }
);

export const getControlSystemModels = createAsyncThunk(
  "customerMachinery/getControlSystemModels",
  async (controlSystemId: string) => {
    return customerMachineryApi.getControlSystemModels(controlSystemId);
  }
);

export const getTypes = createAsyncThunk(
  "customerMachinery/getTypes",
  async () => {
    return customerMachineryApi.getTypes();
  }
);

export const getAutomationLevels = createAsyncThunk(
  "customerMachinery/getAutomationLevels",
  async () => {
    return customerMachineryApi.getAutomationLevels();
  }
);

// --- Decommission thunks ---

export const getDecommissionPreview = createAsyncThunk(
  "customerMachinery/getDecommissionPreview",
  async (machineId: string) => {
    return customerMachineryApi.getDecommissionPreview(machineId);
  }
);

export const decommissionMachine = createAsyncThunk(
  "customerMachinery/decommissionMachine",
  async ({ id, reason, forceArchiveRfs }: { id: string; reason?: string; forceArchiveRfs?: boolean }) => {
    await customerMachineryApi.decommissionMachine(id, { reason, forceArchiveRfs });
    return id;
  }
);

export const restoreMachine = createAsyncThunk(
  "customerMachinery/restoreMachine",
  async (id: string) => {
    await customerMachineryApi.restoreMachine(id);
    return id;
  }
);

export const getDecommissionedMachines = createAsyncThunk(
  "customerMachinery/getDecommissionedMachines",
  async () => {
    return customerMachineryApi.getDecommissionedMachines();
  }
);

const initialState: CustomerMachineryState = {
  query: {
    manufacturerId: undefined,
    typeId: undefined,
    search: undefined
  },
  machines: {},
  machine: undefined,
  manufacturers: [],
  types: [],
  operationTypes: [],
  axisConfigurations: [],
  controlSystemBrands: [],
  controlSystemModels: [],
  automationLevels: [],
  loading: false,
  success: undefined,
  error: undefined,
  // Decommission state
  decommissionedMachines: [],
  decommissionedLoading: false,
  decommissionPreview: null,
  decommissionPreviewLoading: false,
};

const customerMachinerySlice = createSlice({
  name: "customerMachinery",
  initialState: initialState,
  reducers: {
    changeQuery: (state, action) => {
      state.query = action.payload;
    },
    selectMachine: (state, action) => {
      state.machine = action.payload;
    },
    clearMachine: (state) => {
      state.machine = undefined;
    },
    setSuccess: (state, action) => {
      state.success = action.payload;
    },
    setLoading: (state, { payload }) => {
      state.loading = payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    /**
     * Clear cached machines so the next mount of Machinery triggers a fresh
     * API fetch.  Dispatched from LocationsPage after a location rename /
     * disable / create so machine cards pick up updated location names.
     */
    invalidateMachines: (state) => {
      state.machines = {};
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getMachines.pending, (state) => {
        state.loading = true;
      })
      .addCase(getMachines.fulfilled, (state, { payload }) => {
        state.machines = payload;
        state.loading = false;
      })
      .addCase(getMachines.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(getMachine.pending, (state) => {
        state.loading = true;
      })
      .addCase(getMachine.fulfilled, (state, { payload }) => {
        state.machine = payload;
        state.loading = false;
      })
      .addCase(getMachine.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(createMachine.pending, (state) => {
        state.loading = true;
      })
      .addCase(createMachine.fulfilled, (state, { payload }) => {
        state.loading = false;
      })
      .addCase(createMachine.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(changeMachine.pending, (state) => {
        state.loading = true;
      })
      .addCase(changeMachine.fulfilled, (state, { payload }) => {
        state.loading = false;
      })
      .addCase(changeMachine.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(deleteMachine.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteMachine.fulfilled, (state, { payload }) => {
        state.loading = false;
      })
      .addCase(deleteMachine.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(getManufacturers.fulfilled, (state, { payload }) => {
        state.manufacturers = payload;
      });

    builder
      .addCase(getOperationTypes.fulfilled, (state, { payload }) => {
        state.operationTypes = payload;
      });

    builder
      .addCase(getAxisConfigurations.fulfilled, (state, { payload }) => {
        state.axisConfigurations = payload;
      });

    builder
      .addCase(getControlSystemBrands.fulfilled, (state, { payload }) => {
        state.controlSystemBrands = payload;
      });

    builder
      .addCase(getControlSystemModels.fulfilled,(state, { payload }) => {
        state.controlSystemModels = payload;
      });

    builder
      .addCase(getAutomationLevels.fulfilled,(state, { payload }) => {
        state.automationLevels = payload;
      });

    builder
      .addCase(getTypes.fulfilled, (state, { payload }) => {
        state.types = payload.filter(x => x.name === "CNC Machine" || x.name === "Production line");
      });

    // --- Decommission reducers ---
    builder
      .addCase(getDecommissionPreview.pending, (state) => {
        state.decommissionPreviewLoading = true;
        state.decommissionPreview = null;
      })
      .addCase(getDecommissionPreview.fulfilled, (state, { payload }) => {
        state.decommissionPreview = payload;
        state.decommissionPreviewLoading = false;
      })
      .addCase(getDecommissionPreview.rejected, (state, { error }) => {
        state.decommissionPreviewLoading = false;
        state.error = error;
      });

    builder
      .addCase(decommissionMachine.pending, (state) => {
        state.loading = true;
      })
      .addCase(decommissionMachine.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(decommissionMachine.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(restoreMachine.pending, (state) => {
        state.loading = true;
      })
      .addCase(restoreMachine.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(restoreMachine.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(getDecommissionedMachines.pending, (state) => {
        state.decommissionedLoading = true;
      })
      .addCase(getDecommissionedMachines.fulfilled, (state, { payload }) => {
        state.decommissionedMachines = payload;
        state.decommissionedLoading = false;
      })
      .addCase(getDecommissionedMachines.rejected, (state, { error }) => {
        state.error = error;
        state.decommissionedLoading = false;
      });
  }
});

export const {
  changeQuery,
  selectMachine,
  clearMachine,
  setSuccess,
  setLoading,
  setError,
  invalidateMachines,
} = customerMachinerySlice.actions;

export default customerMachinerySlice.reducer;